import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { organizationKeys } from '@/hooks/queries/useOrganizationsQuery';
import { toast } from 'sonner';

export const paymentKeys = {
  all: ['payments'] as const,
  packages: (organizationId: number) => [...paymentKeys.all, 'packages', organizationId] as const,
  list: (organizationId: number, page: number, pageSize: number, sortBy: string, sortDirection: string) =>
    [...paymentKeys.all, 'list', organizationId, page, pageSize, sortBy, sortDirection] as const,
  transferTargets: (organizationId: number) => [...paymentKeys.all, 'transferTargets', organizationId] as const,
};

/**
 * Hook to fetch active credit packages for a project
 */
export const useCreditPackagesQuery = (organizationId: number) => {
  return useQuery({
    queryKey: paymentKeys.packages(organizationId),
    queryFn: () => apiClient.getCreditPackages(organizationId),
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!organizationId,
  });
};

/**
 * Hook to fetch a paginated, sorted page of payment history for a project.
 * Returns the full PagedResult envelope (items, totalCount, totalPages, etc.).
 */
export const usePaymentsQuery = (
  organizationId: number,
  page: number,
  pageSize: number,
  sortBy: string,
  sortDirection: 'asc' | 'desc'
) => {
  return useQuery({
    queryKey: paymentKeys.list(organizationId, page, pageSize, sortBy, sortDirection),
    queryFn: () => apiClient.getPayments(organizationId, page, pageSize, sortBy, sortDirection),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!organizationId,
  });
};

/**
 * Hook to create a Stripe Checkout Session
 */
export const useCreateCheckoutMutation = () => {
  return useMutation({
    mutationFn: ({ organizationId, creditPackageId }: { organizationId: number; creditPackageId: number }) =>
      apiClient.createCheckoutSession(organizationId, creditPackageId),
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error('Failed to create checkout session:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
    },
  });
};

/**
 * Hook to cancel a pending payment when the user cancels Stripe checkout.
 * Invalidates the payments list on success so the Cancelled status appears immediately.
 */
export const useCancelPaymentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, sessionId, rowVersion }: { organizationId: number; sessionId: string; rowVersion?: number }) =>
      apiClient.cancelPayment(organizationId, sessionId, rowVersion),
    onSuccess: (_, { organizationId }) => {
      // Invalidate all list queries for this project (partial key match)
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'list', organizationId] });
    },
    onError: (error) => {
      console.error('Failed to cancel payment:', error);
    },
  });
};

/**
 * Hook to fetch the projects the current user owns that can receive a credit transfer.
 */
export const useTransferTargetsQuery = (organizationId: number, enabled = true) => {
  return useQuery({
    queryKey: paymentKeys.transferTargets(organizationId),
    queryFn: () => apiClient.getTransferTargets(organizationId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!organizationId && enabled,
  });
};

/**
 * Hook to transfer credits from one project to another.
 * Invalidates payment history and organization queries for both projects so balances refresh.
 */
export const useTransferCreditsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      organizationId,
      targetOrganizationId,
      amount,
    }: {
      organizationId: number;
      targetOrganizationId: number;
      amount: number;
    }) => apiClient.transferCredits(organizationId, targetOrganizationId, amount),
    onSuccess: (_data, { organizationId, targetOrganizationId }) => {
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'list', organizationId] });
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'list', targetOrganizationId] });
      queryClient.invalidateQueries({ queryKey: paymentKeys.transferTargets(organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(organizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(targetOrganizationId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
};
